import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';

import { useProcessingSet } from '../../../../../../hooks';
import { trackPhotoUploaded } from '../../../../../../services/analytics/marketplaceEvents';
import { getApiErrorMessage } from '../../../../../../services/marketplace/errors';
import { useMarketplaceExternalId } from '../../../../../../services/marketplace/externalId';
import { useDeleteLeadByExternalIdPhotosPhotoId } from '../../../../../../services/marketplace/generated/kubb/hooks/useDeleteLeadByExternalIdPhotosPhotoId';
import { useGetLeadByExternalId } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import {
  getLeadByExternalIdPhotosQueryKey,
  useGetLeadByExternalIdPhotos,
} from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPhotos';
import { usePostLeadByExternalIdPhotos } from '../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdPhotos';
import { createErrorToast } from '../../../../../../utils/toast';
import { PHOTO_SLOT_COUNT } from '../../constants';
import { deriveListingStatus, isLeadOnMarketplacePath } from '../../listingStatus';
import { buildPhotoSlotMap } from '../buildPhotoSlotMap';
import { MAX_PHOTO_SIZE_BYTES, MAX_PHOTO_SIZE_MB, PHOTO_SLOTS } from '../constants';

type UsePhotoUploadOptions = {
  enforceMinimumPhoto?: boolean;
};

const validatePhotoFile = (file: File): { title: string; description: string } | null => {
  if (file.size >= MAX_PHOTO_SIZE_BYTES) {
    return { title: 'File too large', description: `Photos must be under ${MAX_PHOTO_SIZE_MB}MB.` };
  }
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return { title: 'Unsupported file type', description: 'Please upload an image file.' };
  }
  return null;
};

export const usePhotoUpload = (dealId: string, options: UsePhotoUploadOptions = {}) => {
  const { enforceMinimumPhoto = true } = options;
  const externalId = useMarketplaceExternalId(dealId);

  const {
    processing: processingSlotIds,
    isProcessing: isSlotProcessing,
    beginProcessing: beginSlotProcessing,
    endProcessing: endSlotProcessing,
  } = useProcessingSet<string>();
  const deleteInProgressRef = useRef(false);
  const [lastFilledSlotId, setLastFilledSlotId] = useState<string | null>(null);
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const leadQuery = useGetLeadByExternalId({ externalId });
  const photosQuery = useGetLeadByExternalIdPhotos({ externalId });
  const { mutateAsync: uploadPhoto } = usePostLeadByExternalIdPhotos();
  const { mutateAsync: deletePhoto } = useDeleteLeadByExternalIdPhotosPhotoId();
  const toast = useToast();

  const photos = useMemo(() => buildPhotoSlotMap(photosQuery.data ?? []), [photosQuery.data]);

  const uploadedCount = Math.min(photos.size, PHOTO_SLOT_COUNT);

  const refetchPhotos = useCallback(async () => {
    if (!externalId) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: getLeadByExternalIdPhotosQueryKey({ externalId }),
    });
  }, [externalId, queryClient]);

  const handleSlotClick = useCallback(
    (slotId: string) => {
      if (isSlotProcessing(slotId)) {
        return;
      }

      setActiveSlotId(slotId);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
    },
    [isSlotProcessing],
  );

  const processFile = useCallback(
    async (file: File, slotId: string): Promise<void> => {
      if (!beginSlotProcessing(slotId)) {
        return;
      }

      const validationError = validatePhotoFile(file);
      if (validationError) {
        toast(createErrorToast(validationError));
        endSlotProcessing(slotId);
        return;
      }

      try {
        if (!externalId) {
          throw new Error('Missing marketplace external ID');
        }

        const slotDef = PHOTO_SLOTS.find((s) => s.id === slotId);
        const existingPhoto = photos.get(slotId);
        const uploadedPhoto = await uploadPhoto({
          externalId,
          data: {
            file,
            label: slotId,
            photoType: slotDef?.photoType,
          },
        });
        const nextUploadedCount = uploadedCount + (existingPhoto ? 0 : 1);
        trackPhotoUploaded(dealId, slotId, slotDef?.category ?? '', nextUploadedCount);

        if (existingPhoto && existingPhoto.photoId !== uploadedPhoto.id) {
          try {
            await deletePhoto({
              externalId,
              photoId: existingPhoto.photoId,
            });
          } catch {
            toast({
              position: 'top',
              duration: 4000,
              isClosable: true,
              title: 'Uploaded, but could not remove previous photo',
              description: 'Refresh if this slot still shows the old photo.',
              status: 'warning',
            });
          }
        }

        await refetchPhotos();
        setLastFilledSlotId(slotId);
      } catch (err) {
        toast(
          createErrorToast({
            errorMessage: getApiErrorMessage(err, 'There was an error uploading your photo.'),
          }),
        );
      } finally {
        endSlotProcessing(slotId);
      }
    },
    [
      beginSlotProcessing,
      deletePhoto,
      endSlotProcessing,
      externalId,
      photos,
      refetchPhotos,
      toast,
      uploadPhoto,
    ],
  );

  const handleFileSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !activeSlotId) return;

      if (isSlotProcessing(activeSlotId)) {
        setActiveSlotId(null);
        return;
      }

      await processFile(file, activeSlotId);
      setActiveSlotId(null);
    },
    [activeSlotId, isSlotProcessing, processFile],
  );

  const handleFileDrop = useCallback(
    async (slotId: string, file: File) => {
      await processFile(file, slotId);
    },
    [processFile],
  );

  const handleDelete = useCallback(
    async (slotId: string) => {
      if (deleteInProgressRef.current) return;

      const entry = photos.get(slotId);
      if (!entry || !externalId) return;

      const slotDef = PHOTO_SLOTS.find((s) => s.id === slotId);
      const slotLabel = slotDef?.label ?? 'Photo';

      if (enforceMinimumPhoto && photos.size <= 1) {
        toast({
          position: 'top',
          duration: 3000,
          isClosable: true,
          title: 'You must always have at least one photo.',
          status: 'warning',
        });
        return;
      }

      // Serialize deletes so two rapid clicks on different slots can't both bypass the minimum-photo guard.
      deleteInProgressRef.current = true;
      if (!beginSlotProcessing(slotId)) {
        deleteInProgressRef.current = false;
        return;
      }

      try {
        await deletePhoto({
          externalId,
          photoId: entry.photoId,
        });
        await refetchPhotos();

        toast({
          position: 'top',
          duration: 3000,
          isClosable: true,
          title: `${slotLabel} photo removed`,
          status: 'info',
        });
      } catch (err) {
        toast(
          createErrorToast({
            errorMessage: getApiErrorMessage(err, 'There was an error removing your photo.'),
          }),
        );
      } finally {
        endSlotProcessing(slotId);
        deleteInProgressRef.current = false;
      }
    },
    [
      beginSlotProcessing,
      deletePhoto,
      endSlotProcessing,
      enforceMinimumPhoto,
      externalId,
      photos,
      refetchPhotos,
      toast,
    ],
  );

  const handleReplace = useCallback(
    (slotId: string) => {
      handleSlotClick(slotId);
    },
    [handleSlotClick],
  );

  const clearLastFilled = useCallback(() => {
    setLastFilledSlotId(null);
  }, []);

  const isListed = isLeadOnMarketplacePath(leadQuery.data);
  const listingStatus = deriveListingStatus(leadQuery.data);

  const loadError = leadQuery.error ?? photosQuery.error ?? null;

  const retryLoad = useCallback(async () => {
    await Promise.all([leadQuery.refetch(), photosQuery.refetch()]);
  }, [leadQuery, photosQuery]);

  return {
    loadError,
    isRetryingLoad: leadQuery.isFetching || photosQuery.isFetching,
    retryLoad,
    photos,
    photosReady: !!photosQuery.data,
    processingSlotIds,
    lastFilledSlotId,
    uploadedCount,
    isListed,
    listingStatus,
    fileInputRef,
    handleSlotClick,
    handleFileSelected,
    handleFileDrop,
    handleDelete,
    handleReplace,
    clearLastFilled,
  };
};
